import {
  AddressSetting,
  LISTING_SETTING_CATEGORY,
  UpsertListingEntry,
} from 'src/shared/services/listing-optimization-settings.service';
import { AmenityOption, ListingPhoto, ListingPhotoRoom, ListingRoomBed, UNIFIED_AMENITIES } from 'src/shared/services/property-listings-mock.service';

export type ListingPlaceType = 'entire_home' | 'private_room' | 'shared_room';

/** One listing highlight — a short title + body pair shown near the top of a listing. */
export interface ListingHighlight {
  title: string;
  body: string;
}

/** The `place_type` vocabulary (AddressSetting::SYNC_MATRIX). */
export const LISTING_PLACE_TYPES: Array<{ value: ListingPlaceType; viewValue: string }> = [
  { value: 'entire_home', viewValue: 'Entire home' },
  { value: 'private_room', viewValue: 'Private room' },
  { value: 'shared_room', viewValue: 'Shared room' },
];

/**
 * The `cancellation_policy` tiers every platform accepts (same set Booking Settings
 * edits). Non-refundable is NOT a tier — the platforms model it as a separate rate
 * toggle, which stays on Booking Settings.
 */
export const LISTING_CANCELLATION_POLICIES = [
  { value: 'flexible', viewValue: 'Flexible' },
  { value: 'moderate', viewValue: 'Moderate' },
  { value: 'firm', viewValue: 'Firm' },
  { value: 'strict', viewValue: 'Strict' },
];

/** The address record's own property_type vocabulary (Address::PROPERTY_TYPES). */
export const LISTING_PROPERTY_TYPE_ITEMS = [
  { value: 'single_family', viewValue: 'Single Family' },
  { value: 'condo', viewValue: 'Condo' },
  { value: 'townhouse', viewValue: 'Townhouse' },
  { value: 'multi_family', viewValue: 'Multi Family' },
];

/**
 * Safety chips. The two alarms are unified AMENITIES (they already import from and push
 * to the platforms through the amenities blob), so their chips read/write amenity state;
 * only the two disclosures live in the `safety_items` row. One key per fact.
 */
export const LISTING_SAFETY_ITEMS: AmenityOption[] = [
  { slug: 'smokeAlarm', label: 'Smoke alarm', icon: 'sensors' },
  { slug: 'coAlarm', label: 'Carbon monoxide alarm', icon: 'co2' },
  { slug: 'pool_no_gate', label: 'Pool/hot tub without gate', icon: 'pool' },
  { slug: 'nearby_water', label: 'Nearby water', icon: 'water' },
];

/** The safety slugs that are amenities (see LISTING_SAFETY_ITEMS). */
export const AMENITY_SAFETY_SLUGS = ['smokeAlarm', 'coAlarm'];

/** Canonical bed-type slugs for a bedroom's `beds` entries (PMS vocabulary). */
export const LISTING_BED_TYPES = [
  { value: 'queen', viewValue: 'Queen' },
  { value: 'king', viewValue: 'King' },
  { value: 'double', viewValue: 'Double' },
  { value: 'single', viewValue: 'Single' },
  { value: 'sofa_bed', viewValue: 'Sofa bed' },
];

/** Mirrors AddressSetting::HIGHLIGHTS_MAX — the upsert rejects more. */
export const MAX_LISTING_HIGHLIGHTS = 3;

/** The Listing Details fields with a real backend home (price/property_type/photos excluded). */
export interface ListingContentValues {
  title: string;
  description: string;
  /** Dollars in the form; stored as integer cents under `security_deposit`. */
  securityDepositDollars: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  maxGuests: number | null;
  squareFeet: number | null;
  placeType: string;
  neighborhoodDescription: string;
  houseRules: string[];
  /** The `safety_items` disclosure slugs only — the alarm chips ride `amenities`. */
  safetyItems: string[];
  highlights: ListingHighlight[];
  /** Canonical amenity slug -> enabled, matching the PMS `amenities` shape. */
  amenities: Record<string, boolean>;
}

// The form field -> setting key mapping for the numeric/stored-only fields.
const NUMBER_KEY_BY_FIELD: ReadonlyArray<[keyof ListingContentValues, string]> = [
  ['bedrooms', 'bedrooms'],
  ['bathrooms', 'bathrooms'],
  ['maxGuests', 'max_guests'],
  ['squareFeet', 'square_feet'],
];

/**
 * Builds the `category: listing` upsert entries from the edit form. Blank text, null
 * numbers and an empty amenities map are omitted (an unset field is an absent row, not a
 * cleared one).
 *
 * The lists (house rules, safety items, highlights) and the security deposit are the
 * exceptions: an absent key leaves the stored row untouched, so a list that was loaded with
 * entries and is now empty is sent as `[]`, and a deposit that was loaded and is now blank
 * is sent as 0 (no deposit). `previous` is what the form loaded with.
 */
export function listingContentToEntries(
  values: ListingContentValues,
  previous?: ListingContentValues
): UpsertListingEntry[] {
  const entries: UpsertListingEntry[] = [];
  const push = (key: string, value: unknown): void => { entries.push({ key, data: { value } }); };
  const pushList = (key: string, list: unknown[], loaded: unknown[] | undefined): void => {
    if (list.length > 0 || (loaded?.length ?? 0) > 0) { push(key, list); }
  };

  if (values.title.trim() !== '') { push('title', values.title); }
  if (values.description.trim() !== '') { push('description', values.description); }
  if (values.securityDepositDollars !== null) {
    push('security_deposit', Math.round(values.securityDepositDollars * 100));
  } else if (previous?.securityDepositDollars) {
    push('security_deposit', 0);
  }
  for (const [field, key] of NUMBER_KEY_BY_FIELD) {
    const value = values[field];
    if (value !== null && value !== undefined) { push(key, value); }
  }
  if (Object.keys(values.amenities).length > 0) { push('amenities', values.amenities); }
  // Like the blank-text fields, an unpicked enum is an absent row, not a cleared one —
  // the upsert rejects a blank on these keys.
  if (values.placeType !== '') { push('place_type', values.placeType); }
  if (values.neighborhoodDescription.trim() !== '') {
    push('neighborhood_description', values.neighborhoodDescription);
  }
  pushList('house_rules', values.houseRules, previous?.houseRules);
  pushList('safety_items', values.safetyItems, previous?.safetyItems);
  pushList('highlights', values.highlights.slice(0, MAX_LISTING_HIGHLIGHTS), previous?.highlights);
  return entries;
}

/** The setting key the room-grouped photo gallery lives under (category `listing`). */
export const PHOTOS_SETTING_KEY = 'photos';

/**
 * Stored shape of the `photos` listing row. The backend reads only `rooms`
 * (PmsListingPayload#ordered_rooms) and re-adds `mode: 'replace'` itself before pushing to
 * the platforms, but we persist `mode` too so the stored value stays self-describing.
 */
export interface PhotosSettingValue {
  mode?: string;
  rooms: ListingPhotoRoom[];
}

/**
 * The empty room scaffold shown when a listing has no stored photos yet, so the user always
 * has somewhere to add them. Mirrors the common rooms the gallery groups by.
 *
 * Each room carries an explicit `type`: the platform resolves a photo to a native room "space"
 * by `type` (falling back to a `<type>_<n>` key parse), and `living_room`/`kitchen` don't match
 * that parse — so without a type their photos are rejected at the assign step. See
 * PHOTO_ROOM_TYPE_BY_KEY.
 */
export const DEFAULT_LISTING_PHOTO_ROOMS: ReadonlyArray<Pick<ListingPhotoRoom, 'key' | 'name' | 'type' | 'order'>> = [
  { key: 'living_room', name: 'Living Room', type: 'living_room', order: 0 },
  { key: 'kitchen', name: 'Kitchen', type: 'kitchen', order: 1 },
  { key: 'bedroom_1', name: 'Bedroom 1', type: 'bedroom', order: 2 },
  { key: 'bathroom_1', name: 'Bathroom 1', type: 'bathroom', order: 3 },
];

/**
 * Canonical room key -> platform-resolvable room type, used to backfill a `type` on rooms that
 * arrive without one (e.g. galleries saved before types were sent). Values match the platform's
 * resolvable space types (living_room/kitchen/bedroom/bathroom).
 */
const PHOTO_ROOM_TYPE_BY_KEY: Record<string, string> = {
  living_room: 'living_room',
  kitchen: 'kitchen',
  bedroom_1: 'bedroom',
  bathroom_1: 'bathroom',
};

/**
 * Reads the `photos` listing row's rooms back into the page model, coercing each room and
 * photo to safe defaults and ordering by the stored `order`. Returns null when there's no
 * stored gallery (a fresh listing) so the caller can fall back to the default room scaffold.
 */
export function listingPhotosFromSettings(rows: AddressSetting[] | undefined): ListingPhotoRoom[] | null {
  const row = (rows ?? []).find(r => r.category === LISTING_SETTING_CATEGORY && r.key === PHOTOS_SETTING_KEY);
  const rooms = (row?.data?.['value'] as PhotosSettingValue | undefined)?.rooms;
  if (!Array.isArray(rooms)) { return null; }
  return rooms
    .map((room, index) => normalizeRoom(room, index))
    .sort((a, b) => a.order - b.order);
}

function normalizeRoom(room: any, index: number): ListingPhotoRoom {
  const photos: unknown[] = Array.isArray(room?.photos) ? room.photos : [];
  const beds = normalizeBeds(room?.beds);
  return {
    key: String(room?.key ?? `room_${index}`),
    name: String(room?.name ?? ''),
    ...(room?.type ? { type: String(room.type) } : {}),
    ...(beds.length ? { beds } : {}),
    order: typeof room?.order === 'number' ? room.order : index,
    photos: photos
      .map((photo, photoIndex) => normalizePhoto(photo, photoIndex))
      .sort((a, b) => a.order - b.order),
  };
}

/** Coerces a stored room's `beds` to well-formed entries, dropping empty/zero-count ones. */
function normalizeBeds(beds: unknown): ListingRoomBed[] {
  if (!Array.isArray(beds)) { return []; }
  return beds
    .map(bed => ({
      type: String((bed as any)?.type ?? ''),
      count: typeof (bed as any)?.count === 'number' ? (bed as any).count : 0,
    }))
    .filter(bed => bed.type !== '' && bed.count > 0);
}

function normalizePhoto(photo: any, index: number): ListingPhoto {
  return {
    id: String(photo?.id ?? ''),
    url: String(photo?.url ?? ''),
    caption: typeof photo?.caption === 'string' ? photo.caption : '',
    order: typeof photo?.order === 'number' ? photo.order : index,
  };
}

/**
 * Builds the `photos` upsert entry from the page's rooms, renumbering room/photo `order`
 * from their current array position so a drag-drop reorder persists. Returns null when there
 * isn't a single photo — an untouched empty gallery is an absent row, never a `replace` that
 * would wipe the platform galleries.
 */
export function listingPhotosToEntry(rooms: ListingPhotoRoom[]): UpsertListingEntry | null {
  if (!rooms.some(room => room.photos.length > 0)) { return null; }
  const value: PhotosSettingValue = {
    mode: 'replace',
    rooms: rooms.map((room, roomIndex) => {
      // The platform needs a `type` to place a photo in a native room space; backfill it from
      // the room key when the room didn't carry one. Beds are absent on purpose: they are
      // room STRUCTURE and ride the `spaces` row (listingSpacesToEntry), not the gallery.
      const type = room.type ?? PHOTO_ROOM_TYPE_BY_KEY[room.key];
      return {
        key: room.key,
        name: room.name,
        ...(type ? { type } : {}),
        order: roomIndex,
        photos: room.photos.map((photo, photoIndex) => ({
          id: photo.id,
          url: photo.url,
          caption: photo.caption,
          order: photoIndex,
        })),
      };
    }),
  };
  return { key: PHOTOS_SETTING_KEY, data: { value } };
}

/**
 * Reads the address's `category: listing` rows back into form values (cents -> dollars).
 * Missing rows fall back to blank/null so a fresh listing renders empty.
 */
export function listingContentFromSettings(rows: AddressSetting[] | undefined): ListingContentValues {
  const byKey = new Map<string, unknown>();
  for (const row of rows ?? []) {
    if (row.category === LISTING_SETTING_CATEGORY) { byKey.set(row.key, row.data?.['value']); }
  }
  const str = (key: string): string => (typeof byKey.get(key) === 'string' ? byKey.get(key) as string : '');
  const num = (key: string): number | null => (typeof byKey.get(key) === 'number' ? byKey.get(key) as number : null);
  const cents = byKey.get('security_deposit');
  const amenities = byKey.get('amenities');

  const strList = (key: string): string[] => {
    const value = byKey.get(key);
    return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
  };

  return {
    title: str('title'),
    description: str('description'),
    // 0 means no deposit, so it loads as the blank field that clears it.
    securityDepositDollars: typeof cents === 'number' && cents > 0 ? cents / 100 : null,
    bedrooms: num('bedrooms'),
    bathrooms: num('bathrooms'),
    maxGuests: num('max_guests'),
    squareFeet: num('square_feet'),
    placeType: str('place_type'),
    neighborhoodDescription: str('neighborhood_description'),
    houseRules: strList('house_rules'),
    safetyItems: strList('safety_items'),
    highlights: highlightsFromValue(byKey.get('highlights')),
    amenities: amenities && typeof amenities === 'object' ? { ...(amenities as Record<string, boolean>) } : {},
  };
}

function highlightsFromValue(value: unknown): ListingHighlight[] {
  if (!Array.isArray(value)) { return []; }
  return value
    .map(entry => ({
      title: typeof (entry as any)?.title === 'string' ? (entry as any).title : '',
      body: typeof (entry as any)?.body === 'string' ? (entry as any).body : '',
    }))
    .filter(entry => entry.title !== '' || entry.body !== '')
    .slice(0, MAX_LISTING_HIGHLIGHTS);
}

/** The setting key the sleeping-spaces structure lives under (category `listing`). */
export const SPACES_SETTING_KEY = 'spaces';

/**
 * Stored shape of the `spaces` listing row: the room STRUCTURE (with per-room bed
 * types), seeded from the platform on first connect. The backend re-adds
 * `mode: 'reconcile'` when pushing, mirroring how `photos` re-adds `replace`.
 */
export interface SpacesSettingValue {
  items: Array<Pick<ListingPhotoRoom, 'key' | 'name' | 'type' | 'order' | 'beds'>>;
}

/**
 * Builds the page's room list from the stored rows: the `spaces` row is the room
 * structure (photo-less rooms included), and each room picks up its gallery from the
 * `photos` row by key. Photos-row rooms missing from spaces (galleries saved before
 * spaces existed) are appended so no stored photo is ever hidden. Returns
 * `hasSpacesRow` so the page knows whether a save must write the spaces row back.
 */
export function listingRoomsFromSettings(
  rows: AddressSetting[] | undefined
): { rooms: ListingPhotoRoom[]; hasSpacesRow: boolean } {
  const photoRooms = listingPhotosFromSettings(rows) ?? [];
  const spacesRow = (rows ?? []).find(r => r.category === LISTING_SETTING_CATEGORY && r.key === SPACES_SETTING_KEY);
  const items = (spacesRow?.data?.['value'] as SpacesSettingValue | undefined)?.items;
  if (!Array.isArray(items)) { return { rooms: photoRooms, hasSpacesRow: false }; }

  const photosByKey = new Map(photoRooms.map(room => [room.key, room.photos]));
  const spaceKeys = new Set<string>();
  const rooms = (items as unknown[])
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map((item, index) => {
      const key = String(item['key'] ?? `room_${index}`);
      spaceKeys.add(key);
      return {
        key,
        name: String(item['name'] ?? ''),
        ...(item['type'] ? { type: String(item['type']) } : {}),
        ...(bedsOf(item).length ? { beds: bedsOf(item) } : {}),
        order: typeof item['order'] === 'number' ? item['order'] as number : index,
        photos: photosByKey.get(key) ?? [],
      };
    })
    .sort((a, b) => a.order - b.order);
  const orphanGalleries = photoRooms.filter(room => !spaceKeys.has(room.key));
  return { rooms: [...rooms, ...orphanGalleries], hasSpacesRow: true };
}

function bedsOf(item: Record<string, unknown>): ListingRoomBed[] {
  const beds = item['beds'];
  if (!Array.isArray(beds)) { return []; }
  return beds
    .map(bed => ({
      type: String((bed as any)?.type ?? ''),
      count: typeof (bed as any)?.count === 'number' ? (bed as any).count : 0,
    }))
    .filter(bed => bed.type !== '' && bed.count > 0);
}

/**
 * Builds the `spaces` upsert entry from the page's rooms — every room, photos or not,
 * with zero-count beds dropped and `order` renumbered from array position. The caller
 * decides WHETHER to send it (only when a spaces row already exists or the user edited
 * rooms/beds): the stored doc reconciles authoritatively on the platforms, so a
 * default scaffold must never be pushed as if it were the property's real structure.
 *
 * Every item must carry a type (the PMS edit endpoint hard-rejects a type-less room,
 * failing the whole batched edit), so a missing one backfills from the room key like
 * the photos entry does — and a room with no resolvable type stays out of the doc
 * (its photos still ride the `photos` row).
 */
export function listingSpacesToEntry(rooms: ListingPhotoRoom[]): UpsertListingEntry {
  const value: SpacesSettingValue = {
    items: rooms
      .map(room => ({ room, type: room.type ?? PHOTO_ROOM_TYPE_BY_KEY[room.key] }))
      .filter((entry): entry is { room: ListingPhotoRoom; type: string } => !!entry.type)
      .map(({ room, type }, index) => ({
        key: room.key,
        name: room.name,
        type,
        order: index,
        beds: (room.beds ?? []).filter(bed => bed.count > 0),
      })),
  };
  return { key: SPACES_SETTING_KEY, data: { value } };
}

/** What the Optimizer's Listing Optimization card shows of the listing builder's fields. */
export interface ListingContentSummary {
  title: string;
  description: string;
  amenityCount: number;
  photoCount: number;
}

/**
 * The basic listing fields, read from the same rows the listing builder edits. Amenities
 * count the way the builder's Amenities card does: a row with its own setting (pets)
 * reads that setting, the rest read the amenities blob.
 */
export function listingContentSummary(rows: AddressSetting[] | undefined): ListingContentSummary {
  const content = listingContentFromSettings(rows);
  const amenityCount = UNIFIED_AMENITIES.filter(amenity => amenity.settingKey
    ? (rows ?? []).find(row => row.key === amenity.settingKey)?.data?.['value'] === true
    : !!content.amenities[amenity.slug]
  ).length;
  const photoCount = listingRoomsFromSettings(rows).rooms
    .reduce((sum, room) => sum + room.photos.length, 0);
  return { title: content.title, description: content.description, amenityCount, photoCount };
}
